import { useEffect, lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@futuredev/shared-auth';
import { AdminProtectedRoute, PhoneOnlyGate } from '@futuredev/shared-components';
import AppStore from './views/AppStore/AppStore';
import { CartProvider } from './contexts/CartContext';
import { ActiveCompanyProvider } from './contexts/ActiveCompanyContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ThemedContainer from './components/ThemedContainer';
import PasswordChangeGuard from './components/PasswordChangeGuard';
import AdminNavbar from './components/AdminNavbar';
import SuspenseErrorBoundary from './components/SuspenseErrorBoundary';
import CategoryLayout from './components/CategoryLayout';
import { trackPageView } from './analytics/analyticsClient';

const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ForcePasswordChange = lazy(() => import('./pages/ForcePasswordChange'));
const MagicLinkVerify = lazy(() => import('./pages/MagicLinkVerify'));
const JoinPoolPage = lazy(() => import('./pages/JoinPoolPage'));

const Cart = lazy(() => import('./pages/Cart'));
const Courses = lazy(() => import('./pages/Courses'));
const MembershipDashboard = lazy(() => import('./pages/MembershipDashboard'));
const Products = lazy(() => import('./pages/Products'));

const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const SuperUserDashboard = lazy(() => import('./pages/admin/SuperUserDashboard'));
const TenantManagement = lazy(() => import('./pages/admin/TenantManagement'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const UserEdit = lazy(() => import('./pages/admin/UserEdit'));
const UserIdeaManagement = lazy(() => import('./pages/admin/UserIdeaManagement'));
const UserStatusManagement = lazy(() => import('./pages/UserStatusManagement'));
const IdeaFlowDashboard = lazy(() => import('./pages/admin/IdeaFlowDashboard'));
const CostModelProfiles = lazy(() => import('./pages/admin/CostModelProfiles'));
const EventViewer = lazy(() => import('./pages/admin/EventViewer'));
const WorkflowDesigner = lazy(() => import('./pages/admin/WorkflowDesigner'));
const ProductManagement = lazy(() => import('./pages/admin/ProductManagement'));
const ProductEdit = lazy(() => import('./pages/admin/ProductEdit'));
const CompanyManagement = lazy(() => import('./pages/admin/CompanyManagement'));
const InactiveCompanyManagement = lazy(() => import('./pages/admin/InactiveCompanyManagement'));
const CompanyEdit = lazy(() => import('./pages/admin/CompanyEdit'));
const CompanyDetail = lazy(() => import('./pages/CompanyDetail'));
const CompanyTypeManagement = lazy(() => import('./pages/admin/CompanyTypeManagement'));
const CompanyTypeEdit = lazy(() => import('./pages/admin/CompanyTypeEdit'));
const CountryManagement = lazy(() => import('./pages/admin/CountryManagement'));
const CountryEdit = lazy(() => import('./pages/admin/CountryEdit'));
const ProvinceManagement = lazy(() => import('./pages/admin/ProvinceManagement'));
const ProvinceEdit = lazy(() => import('./pages/admin/ProvinceEdit'));
const StateManagement = lazy(() => import('./pages/admin/StateManagement'));
const StateEdit = lazy(() => import('./pages/admin/StateEdit'));
const RoleManagement = lazy(() => import('./pages/admin/RoleManagement'));
const RoleEdit = lazy(() => import('./pages/admin/RoleEdit'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const SystemAnalytics = lazy(() => import('./pages/admin/SystemAnalytics'));
const DeveloperSettings = lazy(() => import('./pages/admin/DeveloperSettings'));
const BlockchainDashboard = lazy(() => import('./pages/admin/CryptoDashboard'));
const SmartContractEditor = lazy(() => import('./pages/admin/SmartContractEditor'));
const ContractRepository = lazy(() => import('./pages/admin/ContractRepository'));
const BlockchainInspector = lazy(() => import('./pages/admin/BlockchainInspector'));
const InventoryManagement = lazy(() => import('./pages/admin/InventoryManagement'));
const InventoryEdit = lazy(() => import('./pages/admin/InventoryEdit'));
const PaymentManagement = lazy(() => import('./pages/admin/PaymentManagement'));
const CouponManagement = lazy(() => import('./pages/admin/CouponManagement'));
const CouponEdit = lazy(() => import('./pages/admin/CouponEdit'));
const SupplierManagement = lazy(() => import('./pages/admin/SupplierManagement'));
const SupplierEdit = lazy(() => import('./pages/admin/SupplierEdit'));
const ListingManagement = lazy(() => import('./pages/admin/ListingManagement'));
const ListingEdit = lazy(() => import('./pages/admin/ListingEdit'));
const StrainManagement = lazy(() => import('./pages/admin/StrainManagement'));
const StrainEdit = lazy(() => import('./pages/admin/StrainEdit'));
const TagManagement = lazy(() => import('./pages/admin/TagManagement'));
const ACLManagement = lazy(() => import('./pages/admin/ACLManagement'));
const ShippingManagement = lazy(() => import('./pages/admin/ShippingManagement'));
const ShippingCarrierManagement = lazy(() => import('./pages/admin/ShippingCarrierManagement'));
const ShippingCarrierEdit = lazy(() => import('./pages/admin/ShippingCarrierEdit'));
const ServicesManagement = lazy(() => import('./pages/admin/ServicesManagement'));
const DealManagement = lazy(() => import('./pages/admin/DealManagement'));
const KnowledgeBaseManagement = lazy(() => import('./pages/admin/KnowledgeBaseManagement'));
const CourseManagement = lazy(() => import('./pages/admin/CourseManagement'));
const CourseDetail = lazy(() => import('./pages/courses/CourseDetail'));
const LmsTypes = lazy(() => import('./pages/admin/LmsSettings'));
const LmsConfig = lazy(() => import('./pages/admin/LmsConfig'));
const BusinessChannels = lazy(() => import('./pages/admin/BusinessChannels'));
const CourseCategoryTypes = lazy(() => import('./pages/admin/CourseCategoryTypes'));
const BotSettings = lazy(() => import('./pages/admin/BotSettings'));
const UnfinishedOrderSettings = lazy(() => import('./pages/admin/UnfinishedOrderSettings'));
const MembershipManagement = lazy(() => import('./pages/admin/MembershipManagement'));
const KYCProviderManagement = lazy(() => import('./pages/admin/KYCProviderManagement'));
const MailProviderManagement = lazy(() => import('./pages/admin/MailProviderManagement'));
const SmsProviderManagement = lazy(() => import('./pages/admin/SmsProviderManagement'));
const SipProviderManagement = lazy(() => import('./pages/admin/SipProviderManagement'));
const SendSms = lazy(() => import('./pages/admin/SendSms'));
const SmsMessageHistory = lazy(() => import('./pages/admin/SmsMessageHistory'));
const LogViewer = lazy(() => import('./pages/admin/LogViewer'));
const ProjectDashboard = lazy(() => import('./pages/admin/projects/ProjectDashboard'));
const ProjectDetail = lazy(() => import('./pages/admin/projects/ProjectDetail'));
const ProjectTaskBoard = lazy(() => import('./pages/admin/projects/ProjectTaskBoard'));
const ProjectCreate = lazy(() => import('./pages/admin/projects/ProjectCreate'));
const AIEconomicsEngine = lazy(() => import('./pages/admin/projects/AIEconomicsEngine'));
const DockerManagement = lazy(() => import('./pages/admin/DockerManagement'));
const BackupManagement = lazy(() => import('./pages/admin/BackupManagement'));
const AIManagementHub = lazy(() => import('./pages/ai-management/AIManagementHub'));
const DailyQuotesHub = lazy(() => import('./pages/daily-quotes/DailyQuotesHub'));
const LandingCmsHub = lazy(() => import('./pages/landing-cms/LandingCmsHub'));
const SupportTickets = lazy(() => import('./pages/admin/SupportTickets'));
const SupportTicketDetail = lazy(() => import('./pages/admin/SupportTicketDetail'));
const VideoConferenceManagement = lazy(() => import('./pages/admin/VideoConferenceManagement'));
const VideoProviderDeploymentTypeManagement = lazy(() => import('./pages/admin/VideoProviderDeploymentTypeManagement'));
const JanusManagement = lazy(() => import('./pages/admin/JanusManagement'));
const JanusAdmin = lazy(() => import('./pages/admin/JanusAdmin'));
const JanusTranscriptionManagement = lazy(() => import('./pages/admin/JanusTranscriptionManagement'));
const VideoConference = lazy(() => import('./pages/VideoConference'));
const VideoConferenceList = lazy(() => import('./pages/VideoConferenceList'));
const InvestorDashboard = lazy(() => import('./pages/admin/investor/InvestorDashboard'));
const ClientPortalAdmin = lazy(() => import('./pages/admin/client-portal/ClientPortalAdmin'));
const ChatManagement = lazy(() => import('./pages/admin/ChatManagement'));
const Chat = lazy(() => import('./pages/Chat'));
const NavbarEditor = lazy(() => import('./pages/admin/NavbarEditor'));
const SidebarEditor = lazy(() => import('./pages/admin/SidebarEditor'));
const POSDashboard = lazy(() => import('./pages/POSDashboard'));
const NavbarTypesManagement = lazy(() => import('./pages/admin/NavbarTypesManagement'));
import WebsiteBuilder from './pages/admin/WebsiteBuilder';
import AISiteBuilder from './pages/admin/AISiteBuilder';
import DomainManagement from './pages/admin/DomainManagement';
import WebsiteManager from './pages/admin/WebsiteManager';
const WebsitePages = lazy(() => {
  console.log('[App] Lazy loading WebsitePages...');
  return import('./pages/admin/WebsitePages').catch((err: unknown) => {
    console.error('load WebsitePages failed', err);
    return {
      default: () => (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-bold text-red-600 mb-4">Failed to Load Component</h2>
            <p className="text-gray-600 mb-4">The WebsitePages component could not be loaded.</p>
            <p className="text-sm text-gray-500 mb-4">Error: {err instanceof Error ? err.message : 'Unknown error'}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Reload Page
            </button>
          </div>
        </div>
      ),
    };
  });
});
const SystemsControl = lazy(() => import('./pages/admin/SystemsControl'));
const PrometheusDashboard = lazy(() => import('./pages/admin/PrometheusDashboard'));
const ServerManager = lazy(() => import('./pages/admin/ServerManager'));
const RolesEditor = lazy(() => import('./pages/admin/RolesEditor'));
const LoginSecurityManagement = lazy(() => import('./pages/admin/LoginSecurityManagement'));
const PleskDashboard = lazy(() => import('./pages/admin/PleskDashboard'));
const PleskDomains = lazy(() => import('./pages/admin/plesk/PleskDomains'));
const PleskDomainDetail = lazy(() => import('./pages/admin/plesk/PleskDomainDetail'));
const PleskEmail = lazy(() => import('./pages/admin/plesk/PleskEmail'));
const PleskDatabases = lazy(() => import('./pages/admin/plesk/PleskDatabases'));
const PleskClients = lazy(() => import('./pages/admin/plesk/PleskClients'));
const PleskSubscriptions = lazy(() => import('./pages/admin/plesk/PleskSubscriptions'));
const PleskSSL = lazy(() => import('./pages/admin/plesk/PleskSSL'));
const PleskFTP = lazy(() => import('./pages/admin/plesk/PleskFTP'));
const PleskBackups = lazy(() => import('./pages/admin/plesk/PleskBackups'));
const PleskStats = lazy(() => import('./pages/admin/plesk/PleskStats'));

const ESignatureHub = lazy(() => import('./pages/esignature/ESignatureHub'));
const TemplateManagerHub = lazy(() => import('./pages/template-manager/TemplateManagerHub'));
const LegalPortalAdmin = lazy(() => import('./pages/admin/LegalPortalAdmin'));
const CompliancePortal = lazy(() => import('./pages/admin/CompliancePortal'));
const ApplicationTypesHub = lazy(() => import('./pages/application-types/ApplicationTypesHub'));
const GeographicTypesHub = lazy(() => import('./pages/geographic-types/GeographicTypesHub'));
const TemplateTypesHub = lazy(() => import('./pages/template-types/TemplateTypesHub'));
const TaxTypesHub = lazy(() => import('./pages/tax-types/TaxTypesHub'));
const CreateSignatureRequest = lazy(() => import('./pages/esignature/CreateRequest'));

const HRHub = lazy(() => import('./pages/hr/HRHub'));
const CRMHub = lazy(() => import('./pages/crm/CRMHub'));
const CRMRecordPage = lazy(() => import('./pages/crm/CRMRecordPage'));
const AccountingHub = lazy(() => import('./pages/accounting-hub/AccountingHub'));
const SupportHub = lazy(() => import('./pages/support-hub/SupportHub'));
const UserManagementHub = lazy(() => import('./pages/user-management-hub/UserManagementHub'));
const SystemControlsHub = lazy(() => import('./pages/admin/SystemControlsHub'));
const TypesHub = lazy(() => import('./pages/admin/TypesHub'));
const ShippingHub = lazy(() => import('./pages/shipping-hub/ShippingHub'));
const InvestorHub = lazy(() => import('./pages/investor-hub/InvestorHub'));
const AutomotiveHub = lazy(() => import('./pages/automotive-hub/AutomotiveHub'));
const SportsHub = lazy(() => import('./pages/sports-hub/SportsHub'));
const PortfolioHub = lazy(() => import('./pages/portfolio-hub/PortfolioHub'));

const ChartOfAccounts = lazy(() => import('./pages/accounting/ChartOfAccounts'));
const JournalEntries = lazy(() => import('./pages/accounting/JournalEntries'));
const TrialBalance = lazy(() => import('./pages/accounting/TrialBalance'));
const AccountBalance = lazy(() => import('./pages/accounting/AccountBalance'));
const LedgerEntryDetail = lazy(() => import('./pages/accounting/LedgerEntryDetail'));
const Vendors = lazy(() => import('./pages/accounting/Vendors'));
const Customers = lazy(() => import('./pages/accounting/Customers'));
const VendorInvoices = lazy(() => import('./pages/accounting/VendorInvoices'));
const CustomerInvoices = lazy(() => import('./pages/accounting/CustomerInvoices'));
const VendorPayments = lazy(() => import('./pages/accounting/VendorPayments'));
const CustomerPayments = lazy(() => import('./pages/accounting/CustomerPayments'));
const CryptoTransactions = lazy(() => import('./pages/accounting/CryptoTransactions'));
const CryptoHoldings = lazy(() => import('./pages/accounting/CryptoHoldings'));
const PosMapping = lazy(() => import('./pages/inventory/PosMapping'));
const Recipes = lazy(() => import('./pages/inventory/Recipes'));
const MenuItems = lazy(() => import('./pages/inventory/MenuItems'));

const WarehouseManagement = lazy(() => import('./pages/warehouse/WarehouseManagement'));
const StockTransfer = lazy(() => import('./pages/warehouse/StockTransfer'));
const StockTransfers = lazy(() => import('./pages/warehouse/StockTransfers'));

const PurchaseOrderManagement = lazy(() => import('./pages/purchase-order/PurchaseOrderManagement'));
const PurchaseOrderCreate = lazy(() => import('./pages/purchase-order/PurchaseOrderCreate'));
const PurchaseOrderReceive = lazy(() => import('./pages/purchase-order/PurchaseOrderReceive'));

const BankReconciliation = lazy(() => import('./pages/bank-reconciliation/BankReconciliation'));
const BankAccountCreate = lazy(() => import('./pages/bank-reconciliation/BankAccountCreate'));
const BankStatementImport = lazy(() => import('./pages/bank-reconciliation/BankStatementImport'));
const BankStatementReconcile = lazy(() => import('./pages/bank-reconciliation/BankStatementReconcile'));

const LeaveManagement = lazy(() => import('./pages/leave/LeaveManagement'));
const LeaveRequestCreate = lazy(() => import('./pages/leave/LeaveRequestCreate'));
const LeaveRequestDetail = lazy(() => import('./pages/leave/LeaveRequestDetail'));

const CurrencyManagement = lazy(() => import('./pages/currency/CurrencyManagement'));
const ExchangeRateManagement = lazy(() => import('./pages/currency/ExchangeRateManagement'));
const CurrencyConverter = lazy(() => import('./pages/currency/CurrencyConverter'));

const AccountingManagement = lazy(() => import('./pages/admin/AccountingManagement'));
const TaxTypeManagement = lazy(() => import('./pages/accounting/TaxTypeManagement'));
const HRManagement = lazy(() => import('./pages/admin/HRManagement'));
const CommodityMarketplaceManagement = lazy(() => import('./pages/admin/CommodityMarketplaceManagement'));
const MarketplaceManagement = lazy(() => import('./pages/admin/MarketplaceManagement'));
const InvestmentPoolsManagement = lazy(() => import('./pages/admin/InvestmentPoolsManagement'));
const RealEstateAdminHub = lazy(() => import('./pages/real-estate-hub/RealEstateAdminHub'));
const SpecialEventsAdminHub = lazy(() => import('./pages/special-events-hub/SpecialEventsAdminHub'));
const OfferingApprovalQueue = lazy(() => import('./pages/admin/OfferingApprovalQueue'));

// Lifestyle Planning Admin
const LifestyleAdminDashboard = lazy(() => import('./pages/admin/LifestyleAdminDashboard'));
const LifestyleMemberManagement = lazy(() => import('./pages/admin/LifestyleMemberManagement'));
const LifestyleEventManagement = lazy(() => import('./pages/admin/LifestyleEventManagement'));
const LifestyleNotifications = lazy(() => import('./pages/admin/LifestyleNotifications'));
const LifestyleAuditLog = lazy(() => import('./pages/admin/LifestyleAuditLog'));
const LifestyleWorkerOps = lazy(() => import('./pages/admin/LifestyleWorkerOps'));
const LifestyleConflictAdmin = lazy(() => import('./pages/admin/LifestyleConflictAdmin'));
const LifestyleSupportAdmin = lazy(() => import('./pages/admin/LifestyleSupportAdmin'));

const CRMManagement = lazy(() => import('./pages/CRMManagement'));
const CRMContacts = lazy(() => import('./pages/CRMContacts'));
const CRMCompanyInformation = lazy(() => import('./pages/CRMCompanyInformation'));
const CRMLeads = lazy(() => import('./pages/CRMLeads'));
const CRMOpportunities = lazy(() => import('./pages/CRMOpportunities'));
const CRMActivities = lazy(() => import('./pages/CRMActivities'));

const DocumentManagement = lazy(() => import('./pages/DocumentManagement'));
const DocumentStorageQuotas = lazy(() => import('./pages/DocumentStorageQuotas'));
const CloudStorageHub = lazy(() => import('./pages/admin/cloud-storage/CloudStorageHub'));

const Dashboard = lazy(() => import('./pages/Dashboard'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));
const PrivacyNotice = lazy(() => import('./pages/PrivacyNotice'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
      <div className="text-gray-500">Loading...</div>
    </div>
  </div>
);

function AnalyticsRouteTracker(): null {
  const location = useLocation();
  const { loading } = useAuth();

  useEffect(() => {
    if (!loading) {
      trackPageView(location.pathname);
    }
  }, [location.pathname, loading]);

  return null;
}

function ConditionalNavbar(): ReactNode {
  const { user, loading } = useAuth();
  const location = useLocation();

  const publicOnlyPages = ['/login', '/signup', '/forgot-password', '/reset-password', '/magic-link/verify'];
  const isPublicOnlyPage = publicOnlyPages.some(p => location.pathname.startsWith(p));

  const shouldShowNavbar = !!user && !loading && !isPublicOnlyPage;

  if (!shouldShowNavbar) {
    return null;
  }

  return <AdminNavbar />;
}

function App() {
  useEffect(() => {
    const checkLocation = () => {
      const currentUrl = window.location.href;
      if (currentUrl.includes('localhost:3001') || currentUrl.includes('127.0.0.1:3001')) {
        console.warn('[React] Backend URL detected, redirecting...');
        const path = window.location.pathname;
        window.location.replace('http://localhost:5174' + (path === '/' ? '' : path));
        return;
      }
    };

    checkLocation();

    const handlePopState = () => {
      setTimeout(checkLocation, 0);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <CartProvider>
          <ActiveCompanyProvider>
          <Router>
            <ThemedContainer>
              <AnalyticsRouteTracker />
              <PasswordChangeGuard>
                <ConditionalNavbar />
                <SuspenseErrorBoundary fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/magic-link/verify/:token" element={<MagicLinkVerify />} />
                    <Route
                      path="/change-password"
                      element={
                        <AdminProtectedRoute>
                          <ForcePasswordChange />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route path="/pools/join/:token" element={<JoinPoolPage />} />

                    <Route
                      path="/dashboard"
                      element={
                        <AdminProtectedRoute>
                          <Dashboard />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/video-conferences"
                      element={
                        <AdminProtectedRoute>
                          <VideoConferenceList />
                        </AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/video-conference/:roomName?"
                      element={
                        <AdminProtectedRoute>
                          <VideoConference />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/dashboard"
                      element={<Navigate to="/admin" replace />}
                    />
                    <Route
                      path="/admin"
                      element={
                        <AdminProtectedRoute>
                          <AdminDashboard />
                        </AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/super-user-dashboard"
                      element={
                        <AdminProtectedRoute requiredRole="SUPER_USER">
                          <SuperUserDashboard />
                        </AdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/tenants"
                      element={
                        <AdminProtectedRoute requiredRole="SUPER_USER">
                          <TenantManagement />
                        </AdminProtectedRoute>
                      }
                    />

                    <Route path="/admin/investors" element={<Navigate to="/admin/investor-hub" replace />} />

                    <Route element={<CategoryLayout />}>
                      <Route path="/admin/client-portal" element={<AdminProtectedRoute><ClientPortalAdmin /></AdminProtectedRoute>} />
                      <Route path="/admin/users" element={<AdminProtectedRoute><UserManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/users/:id" element={<AdminProtectedRoute><UserEdit /></AdminProtectedRoute>} />
                      <Route path="/admin/login-security" element={<AdminProtectedRoute><LoginSecurityManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/plesk" element={<AdminProtectedRoute><PleskDashboard /></AdminProtectedRoute>} />
                      <Route path="/admin/plesk/domains" element={<AdminProtectedRoute><PleskDomains /></AdminProtectedRoute>} />
                      <Route path="/admin/plesk/domains/:name" element={<AdminProtectedRoute><PleskDomainDetail /></AdminProtectedRoute>} />
                      <Route path="/admin/plesk/email" element={<AdminProtectedRoute><PleskEmail /></AdminProtectedRoute>} />
                      <Route path="/admin/plesk/databases" element={<AdminProtectedRoute><PleskDatabases /></AdminProtectedRoute>} />
                      <Route path="/admin/plesk/clients" element={<AdminProtectedRoute><PleskClients /></AdminProtectedRoute>} />
                      <Route path="/admin/plesk/subscriptions" element={<AdminProtectedRoute><PleskSubscriptions /></AdminProtectedRoute>} />
                      <Route path="/admin/plesk/ssl" element={<AdminProtectedRoute><PleskSSL /></AdminProtectedRoute>} />
                      <Route path="/admin/plesk/ftp" element={<AdminProtectedRoute><PleskFTP /></AdminProtectedRoute>} />
                      <Route path="/admin/plesk/backups" element={<AdminProtectedRoute><PleskBackups /></AdminProtectedRoute>} />
                      <Route path="/admin/plesk/stats" element={<AdminProtectedRoute><PleskStats /></AdminProtectedRoute>} />

                      <Route path="/admin/products" element={<AdminProtectedRoute><ProductManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/app-store" element={<AdminProtectedRoute><AppStore /></AdminProtectedRoute>} />
                      <Route path="/admin/pos" element={<AdminProtectedRoute><POSDashboard /></AdminProtectedRoute>} />
                      <Route path="/admin/compliance" element={<AdminProtectedRoute><CompliancePortal /></AdminProtectedRoute>} />
                      <Route path="/admin/products/:id" element={<AdminProtectedRoute><ProductEdit /></AdminProtectedRoute>} />

                      <Route path="/admin/companies" element={<AdminProtectedRoute><CompanyManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/companies/inactive" element={<AdminProtectedRoute><InactiveCompanyManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/companies/new" element={<AdminProtectedRoute><CompanyEdit /></AdminProtectedRoute>} />
                      <Route path="/admin/companies/:id" element={<AdminProtectedRoute><CompanyDetail /></AdminProtectedRoute>} />
                      <Route path="/admin/companies/:id/edit" element={<AdminProtectedRoute><CompanyEdit /></AdminProtectedRoute>} />
                      <Route path="/admin/company-types" element={<AdminProtectedRoute><CompanyTypeManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/company-types/:id" element={<AdminProtectedRoute><CompanyTypeEdit /></AdminProtectedRoute>} />

                      <Route path="/admin/analytics" element={<AdminProtectedRoute><Analytics /></AdminProtectedRoute>} />
                      <Route path="/admin/user-ideas" element={<AdminProtectedRoute><UserIdeaManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/user-status" element={<AdminProtectedRoute><UserStatusManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/idea-flow" element={<AdminProtectedRoute><IdeaFlowDashboard /></AdminProtectedRoute>} />
                      <Route path="/admin/cost-model-profiles" element={<AdminProtectedRoute><CostModelProfiles /></AdminProtectedRoute>} />
                      <Route path="/admin/event-viewer" element={<AdminProtectedRoute><EventViewer /></AdminProtectedRoute>} />
                      <Route path="/admin/system-analytics" element={<AdminProtectedRoute><SystemAnalytics /></AdminProtectedRoute>} />

                      <Route path="/admin/support-tickets" element={<AdminProtectedRoute><SupportTickets /></AdminProtectedRoute>} />
                      <Route path="/admin/support-tickets/:id" element={<AdminProtectedRoute><SupportTicketDetail /></AdminProtectedRoute>} />

                      <Route path="/admin/video-conferences" element={<AdminProtectedRoute><VideoConferenceManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/janus-management" element={<AdminProtectedRoute><JanusManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/janus-admin" element={<AdminProtectedRoute><JanusAdmin /></AdminProtectedRoute>} />
                      <Route path="/admin/janus-transcription" element={<AdminProtectedRoute><JanusTranscriptionManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/video-provider-deployment-types" element={<AdminProtectedRoute><VideoProviderDeploymentTypeManagement /></AdminProtectedRoute>} />

                      <Route path="/admin/workflow-designer" element={<AdminProtectedRoute requiredRole="SUPER_USER"><PhoneOnlyGate message="Open on a tablet or computer to use the visual workflow editor."><WorkflowDesigner /></PhoneOnlyGate></AdminProtectedRoute>} />

                      <Route path="/admin/esignature" element={<AdminProtectedRoute><ESignatureHub /></AdminProtectedRoute>} />
                      <Route path="/admin/esignature/create" element={<AdminProtectedRoute><CreateSignatureRequest /></AdminProtectedRoute>} />
                      <Route path="/admin/esignature/:id" element={<AdminProtectedRoute><ESignatureHub /></AdminProtectedRoute>} />

                      <Route path="/admin/crypto-dashboard" element={<AdminProtectedRoute><BlockchainDashboard /></AdminProtectedRoute>} />
                      <Route path="/admin/smart-contract-editor/:id?" element={<AdminProtectedRoute><SmartContractEditor /></AdminProtectedRoute>} />
                      <Route path="/admin/contract-repository" element={<AdminProtectedRoute><ContractRepository /></AdminProtectedRoute>} />
                      <Route path="/admin/blockchain-inspector/:id" element={<AdminProtectedRoute><BlockchainInspector /></AdminProtectedRoute>} />

                      <Route path="/admin/countries" element={<AdminProtectedRoute><CountryManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/countries/:id" element={<AdminProtectedRoute><CountryEdit /></AdminProtectedRoute>} />
                      <Route path="/admin/provinces" element={<AdminProtectedRoute><ProvinceManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/provinces/:id" element={<AdminProtectedRoute><ProvinceEdit /></AdminProtectedRoute>} />
                      <Route path="/admin/states" element={<AdminProtectedRoute><StateManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/states/:id" element={<AdminProtectedRoute><StateEdit /></AdminProtectedRoute>} />

                      <Route path="/admin/roles" element={<AdminProtectedRoute><RoleManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/roles-editor" element={<AdminProtectedRoute requiredRole="SUPER_USER"><RolesEditor /></AdminProtectedRoute>} />
                      <Route path="/admin/roles/:id" element={<AdminProtectedRoute><RoleEdit /></AdminProtectedRoute>} />

                      <Route path="/admin/inventory" element={<AdminProtectedRoute><div className="page-shell"><InventoryManagement /></div></AdminProtectedRoute>} />
                      <Route path="/admin/inventory/:id" element={<AdminProtectedRoute><InventoryEdit /></AdminProtectedRoute>} />

                      <Route path="/admin/payments" element={<AdminProtectedRoute><PaymentManagement /></AdminProtectedRoute>} />

                      <Route path="/admin/coupons" element={<AdminProtectedRoute><CouponManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/coupons/:id" element={<AdminProtectedRoute><CouponEdit /></AdminProtectedRoute>} />

                      <Route path="/admin/suppliers" element={<AdminProtectedRoute><SupplierManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/suppliers/:id" element={<AdminProtectedRoute><SupplierEdit /></AdminProtectedRoute>} />

                      <Route path="/admin/listings" element={<AdminProtectedRoute><ListingManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/listings/:id" element={<AdminProtectedRoute><ListingEdit /></AdminProtectedRoute>} />

                      <Route path="/admin/tags" element={<AdminProtectedRoute><TagManagement /></AdminProtectedRoute>} />

                      <Route path="/admin/acl" element={<AdminProtectedRoute><ACLManagement /></AdminProtectedRoute>} />

                      <Route path="/admin/strains" element={<AdminProtectedRoute><StrainManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/strains/:id" element={<AdminProtectedRoute><StrainEdit /></AdminProtectedRoute>} />

                      <Route path="/admin/shipping" element={<AdminProtectedRoute><ShippingManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/shipping-carriers" element={<AdminProtectedRoute><ShippingCarrierManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/shipping-carriers/:id" element={<AdminProtectedRoute><ShippingCarrierEdit /></AdminProtectedRoute>} />

                      <Route path="/admin/services" element={<AdminProtectedRoute><ServicesManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/real-estate" element={<AdminProtectedRoute><RealEstateAdminHub /></AdminProtectedRoute>} />
                      <Route path="/admin/special-events" element={<AdminProtectedRoute><SpecialEventsAdminHub /></AdminProtectedRoute>} />

                      <Route path="/admin/deals" element={<AdminProtectedRoute><DealManagement /></AdminProtectedRoute>} />

                      <Route path="/admin/knowledge-base" element={<AdminProtectedRoute><KnowledgeBaseManagement /></AdminProtectedRoute>} />

                      <Route path="/admin/courses" element={<AdminProtectedRoute><CourseManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/courses/:slug" element={<AdminProtectedRoute><CourseDetail /></AdminProtectedRoute>} />
                      <Route path="/admin/lms-types" element={<AdminProtectedRoute><LmsTypes /></AdminProtectedRoute>} />
                      <Route path="/admin/lms-settings" element={<AdminProtectedRoute><LmsConfig /></AdminProtectedRoute>} />
                      <Route path="/admin/business-channels" element={<AdminProtectedRoute><BusinessChannels /></AdminProtectedRoute>} />
                      <Route path="/admin/course-category-types" element={<AdminProtectedRoute><CourseCategoryTypes /></AdminProtectedRoute>} />

                      <Route path="/admin/memberships" element={<AdminProtectedRoute><MembershipManagement /></AdminProtectedRoute>} />

                      <Route path="/admin/kyc-providers" element={<AdminProtectedRoute><KYCProviderManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/mail-providers" element={<AdminProtectedRoute><MailProviderManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/sms-providers" element={<AdminProtectedRoute><SmsProviderManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/sip-providers" element={<AdminProtectedRoute><SipProviderManagement /></AdminProtectedRoute>} />

                      <Route path="/admin/sms/send" element={<AdminProtectedRoute><SendSms /></AdminProtectedRoute>} />
                      <Route path="/admin/sms/messages" element={<AdminProtectedRoute><SmsMessageHistory /></AdminProtectedRoute>} />

                      <Route path="/admin/log-viewer" element={<AdminProtectedRoute><LogViewer /></AdminProtectedRoute>} />

                      <Route path="/chat" element={<AdminProtectedRoute><Chat /></AdminProtectedRoute>} />
                      <Route path="/admin/chat" element={<AdminProtectedRoute><Chat /></AdminProtectedRoute>} />
                      <Route path="/admin/chat-management/*" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><ChatManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/navbar-editor/:type" element={<AdminProtectedRoute><NavbarEditor /></AdminProtectedRoute>} />
                      <Route path="/admin/sidebar-editor/:type" element={<AdminProtectedRoute><SidebarEditor /></AdminProtectedRoute>} />
                      <Route path="/admin/navbar-types" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><NavbarTypesManagement /></AdminProtectedRoute>} />

                      <Route path="/admin/website-manager" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><WebsiteManager /></AdminProtectedRoute>} />
                      <Route path="/admin/website-pages" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><WebsitePages /></AdminProtectedRoute>} />
                      <Route path="/admin/website-builder/ai" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><AISiteBuilder /></AdminProtectedRoute>} />
                      <Route path="/admin/website-builder/:id?" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><PhoneOnlyGate message="Open on a tablet or computer to use the website builder."><WebsiteBuilder /></PhoneOnlyGate></AdminProtectedRoute>} />
                      <Route path="/admin/domains" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><DomainManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/landing-cms" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><LandingCmsHub /></AdminProtectedRoute>} />

                      <Route path="/admin/projects" element={<AdminProtectedRoute><ProjectDashboard /></AdminProtectedRoute>} />
                      <Route path="/admin/projects/new" element={<AdminProtectedRoute><ProjectCreate /></AdminProtectedRoute>} />
                      <Route path="/admin/projects/economics" element={<AdminProtectedRoute requiredRole="SUPER_USER"><AIEconomicsEngine /></AdminProtectedRoute>} />
                      <Route path="/admin/projects/:projectId" element={<AdminProtectedRoute><ProjectDetail /></AdminProtectedRoute>} />
                      <Route path="/admin/projects/:projectId/tasks" element={<AdminProtectedRoute><ProjectTaskBoard /></AdminProtectedRoute>} />

                      <Route path="/admin/settings/bot" element={<AdminProtectedRoute><BotSettings /></AdminProtectedRoute>} />
                      <Route path="/admin/settings/unfinished-orders" element={<AdminProtectedRoute><UnfinishedOrderSettings /></AdminProtectedRoute>} />

                      <Route path="/admin/docker" element={<AdminProtectedRoute><DockerManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/backup-management" element={<AdminProtectedRoute><BackupManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/systems-control" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><SystemsControl /></AdminProtectedRoute>} />
                      <Route path="/admin/system-controls" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><SystemControlsHub /></AdminProtectedRoute>} />
                      <Route path="/admin/types" element={<AdminProtectedRoute><TypesHub /></AdminProtectedRoute>} />
                      <Route path="/admin/server-manager" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><ServerManager /></AdminProtectedRoute>} />
                      <Route path="/admin/prometheus-dashboard" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><PrometheusDashboard /></AdminProtectedRoute>} />

                      <Route path="/admin/ai-management" element={<AdminProtectedRoute><AIManagementHub /></AdminProtectedRoute>} />
                      <Route path="/admin/daily-quotes" element={<AdminProtectedRoute><DailyQuotesHub /></AdminProtectedRoute>} />

                      <Route path="/admin/template-manager" element={<AdminProtectedRoute><TemplateManagerHub /></AdminProtectedRoute>} />
                      <Route path="/admin/legal-management" element={<AdminProtectedRoute><LegalPortalAdmin /></AdminProtectedRoute>} />

                      <Route path="/admin/application-types" element={<AdminProtectedRoute><ApplicationTypesHub /></AdminProtectedRoute>} />

                      <Route path="/admin/geographic-types" element={<AdminProtectedRoute><GeographicTypesHub /></AdminProtectedRoute>} />

                      <Route path="/admin/template-types" element={<AdminProtectedRoute><TemplateTypesHub /></AdminProtectedRoute>} />

                      <Route path="/admin/tax-types" element={<AdminProtectedRoute><TaxTypesHub /></AdminProtectedRoute>} />

                      <Route path="/admin/hr" element={<AdminProtectedRoute><HRHub /></AdminProtectedRoute>} />
                      <Route path="/admin/crm" element={<AdminProtectedRoute><CRMHub /></AdminProtectedRoute>} />
                      <Route path="/admin/accounting" element={<AdminProtectedRoute><AccountingHub /></AdminProtectedRoute>} />
                      <Route path="/admin/support" element={<AdminProtectedRoute><SupportHub /></AdminProtectedRoute>} />
                      <Route path="/admin/user-management" element={<AdminProtectedRoute><UserManagementHub /></AdminProtectedRoute>} />
                      <Route path="/admin/shipping-hub" element={<AdminProtectedRoute><ShippingHub /></AdminProtectedRoute>} />
                      <Route path="/admin/investor-hub" element={<AdminProtectedRoute><InvestorHub /></AdminProtectedRoute>} />
                      <Route path="/admin/automotive" element={<AdminProtectedRoute><AutomotiveHub /></AdminProtectedRoute>} />
                      <Route path="/admin/sports" element={<AdminProtectedRoute><SportsHub /></AdminProtectedRoute>} />
                      <Route path="/admin/portfolio-hub" element={<AdminProtectedRoute><PortfolioHub /></AdminProtectedRoute>} />

                      <Route path="/admin/documents" element={<AdminProtectedRoute><DocumentManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/cloud-storage" element={<AdminProtectedRoute><CloudStorageHub /></AdminProtectedRoute>} />
                      <Route path="/admin/document-storage-quotas" element={<AdminProtectedRoute><DocumentStorageQuotas /></AdminProtectedRoute>} />

                      <Route path="/admin/accounting/chart-of-accounts" element={<AdminProtectedRoute><ChartOfAccounts /></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/journal-entries" element={<AdminProtectedRoute><JournalEntries /></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/journal-entries/:id" element={<AdminProtectedRoute><LedgerEntryDetail /></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/trial-balance" element={<AdminProtectedRoute><TrialBalance /></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/account-balance" element={<AdminProtectedRoute><AccountBalance /></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/vendors" element={<AdminProtectedRoute><Vendors /></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/customers" element={<AdminProtectedRoute><Customers /></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/vendor-invoices" element={<AdminProtectedRoute><div className="page-shell"><VendorInvoices /></div></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/customer-invoices" element={<AdminProtectedRoute><div className="page-shell"><CustomerInvoices /></div></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/vendor-payments" element={<AdminProtectedRoute><VendorPayments /></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/customer-payments" element={<AdminProtectedRoute><CustomerPayments /></AdminProtectedRoute>} />
                      <Route path="/admin/inventory/pos-mapping" element={<AdminProtectedRoute><div className="page-shell"><PosMapping /></div></AdminProtectedRoute>} />
                      <Route path="/admin/inventory/recipes" element={<AdminProtectedRoute><div className="page-shell"><Recipes /></div></AdminProtectedRoute>} />
                      <Route path="/admin/inventory/menu-items" element={<AdminProtectedRoute><div className="page-shell"><MenuItems /></div></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/crypto-transactions" element={<AdminProtectedRoute><CryptoTransactions /></AdminProtectedRoute>} />
                      <Route path="/admin/accounting/crypto-holdings" element={<AdminProtectedRoute><CryptoHoldings /></AdminProtectedRoute>} />

                      <Route path="/admin/warehouse" element={<AdminProtectedRoute><div className="page-shell"><WarehouseManagement /></div></AdminProtectedRoute>} />
                      <Route path="/admin/warehouse/transfers" element={<AdminProtectedRoute><StockTransfers /></AdminProtectedRoute>} />
                      <Route path="/admin/warehouse/transfers/new" element={<AdminProtectedRoute><StockTransfer /></AdminProtectedRoute>} />

                      <Route path="/admin/purchase-orders" element={<AdminProtectedRoute><PurchaseOrderManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/purchase-orders/new" element={<AdminProtectedRoute><PurchaseOrderCreate /></AdminProtectedRoute>} />
                      <Route path="/admin/purchase-orders/:id/receive" element={<AdminProtectedRoute><PurchaseOrderReceive /></AdminProtectedRoute>} />

                      <Route path="/admin/bank-reconciliation" element={<AdminProtectedRoute><BankReconciliation /></AdminProtectedRoute>} />
                      <Route path="/admin/bank-reconciliation/accounts/new" element={<AdminProtectedRoute><BankAccountCreate /></AdminProtectedRoute>} />
                      <Route path="/admin/bank-reconciliation/import" element={<AdminProtectedRoute><BankStatementImport /></AdminProtectedRoute>} />
                      <Route path="/admin/bank-reconciliation/reconcile" element={<AdminProtectedRoute><BankStatementReconcile /></AdminProtectedRoute>} />

                      <Route path="/admin/leave" element={<AdminProtectedRoute><LeaveManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/leave/requests/new" element={<AdminProtectedRoute><LeaveRequestCreate /></AdminProtectedRoute>} />
                      <Route path="/admin/leave/requests/:id" element={<AdminProtectedRoute><LeaveRequestDetail /></AdminProtectedRoute>} />

                      <Route path="/admin/currency" element={<AdminProtectedRoute><CurrencyManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/currency/exchange-rates" element={<AdminProtectedRoute><ExchangeRateManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/currency/converter" element={<AdminProtectedRoute><CurrencyConverter /></AdminProtectedRoute>} />

                      <Route path="/admin/crm/management" element={<AdminProtectedRoute><CRMManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/crm/contacts/:id" element={<AdminProtectedRoute><CRMRecordPage /></AdminProtectedRoute>} />
                      <Route path="/admin/crm/contacts" element={<AdminProtectedRoute><CRMContacts /></AdminProtectedRoute>} />
                      <Route path="/admin/crm/companies/:companyId" element={<AdminProtectedRoute><CRMCompanyInformation /></AdminProtectedRoute>} />
                      <Route path="/admin/crm/companies" element={<AdminProtectedRoute><CRMCompanyInformation /></AdminProtectedRoute>} />
                      <Route path="/admin/crm/leads/:id" element={<AdminProtectedRoute><CRMRecordPage /></AdminProtectedRoute>} />
                      <Route path="/admin/crm/leads" element={<AdminProtectedRoute><CRMLeads /></AdminProtectedRoute>} />
                      <Route path="/admin/crm/opportunities/:id" element={<AdminProtectedRoute><CRMRecordPage /></AdminProtectedRoute>} />
                      <Route path="/admin/crm/opportunities" element={<AdminProtectedRoute><CRMOpportunities /></AdminProtectedRoute>} />
                      <Route path="/admin/crm/activities" element={<AdminProtectedRoute><CRMActivities /></AdminProtectedRoute>} />

                      <Route path="/admin/tax-type-management" element={<AdminProtectedRoute><TaxTypeManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/accounting-management" element={<AdminProtectedRoute><AccountingManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/commodity-marketplace" element={<AdminProtectedRoute><CommodityMarketplaceManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/marketplace" element={<AdminProtectedRoute requiredRole="ADMIN_OR_STAFF"><MarketplaceManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/investment-pools" element={<AdminProtectedRoute><InvestmentPoolsManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/offering-approvals" element={<AdminProtectedRoute><OfferingApprovalQueue /></AdminProtectedRoute>} />
                      <Route path="/admin/developer" element={<AdminProtectedRoute><DeveloperSettings /></AdminProtectedRoute>} />
                      <Route path="/admin/hr-management" element={<AdminProtectedRoute><HRManagement /></AdminProtectedRoute>} />

                      {/* Lifestyle Planning Admin */}
                      <Route path="/admin/lifestyle" element={<AdminProtectedRoute><LifestyleAdminDashboard /></AdminProtectedRoute>} />
                      <Route path="/admin/lifestyle/members" element={<AdminProtectedRoute><LifestyleMemberManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/lifestyle/events" element={<AdminProtectedRoute><LifestyleEventManagement /></AdminProtectedRoute>} />
                      <Route path="/admin/lifestyle/notifications" element={<AdminProtectedRoute><LifestyleNotifications /></AdminProtectedRoute>} />
                      <Route path="/admin/lifestyle/audit" element={<AdminProtectedRoute><LifestyleAuditLog /></AdminProtectedRoute>} />
                      <Route path="/admin/lifestyle/workers" element={<AdminProtectedRoute><LifestyleWorkerOps /></AdminProtectedRoute>} />
                      <Route path="/admin/lifestyle/conflicts" element={<AdminProtectedRoute><LifestyleConflictAdmin /></AdminProtectedRoute>} />
                      <Route path="/admin/lifestyle/support" element={<AdminProtectedRoute><LifestyleSupportAdmin /></AdminProtectedRoute>} />
                    </Route>

                    <Route path="/" element={<Home />} />

                    <Route path="/terms-of-use" element={<TermsOfUse />} />
                    <Route path="/privacy-notice" element={<PrivacyNotice />} />

                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </Routes>
                </SuspenseErrorBoundary>
              </PasswordChangeGuard>
            </ThemedContainer>
          </Router>
          </ActiveCompanyProvider>
        </CartProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
